import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { type BenefitPreference } from '../../api';
import {
  useSitesOfCare,
  useBenefitPreferences,
  useMedicalBenefitDrugs,
  useDeleteBenefitPreference,
} from '../../hooks/useBenefitPreferences';
import SiteOfCareList from './SiteOfCareList';
import BenefitPreferencesTable from './BenefitPreferencesTable';
import AddSiteOfCareModal from './AddSiteOfCareModal';
import BenefitPreferenceFormModal, { type BenefitPreferenceFormData } from './BenefitPreferenceFormModal';

type ModalState =
  | { type: 'none' }
  | { type: 'addSite' }
  | { type: 'addPreference'; initialData?: Partial<BenefitPreferenceFormData> }
  | { type: 'editPreference'; preference: BenefitPreference };

/**
 * Main tab component for the Benefit Preferences settings tab.
 * Contains two sections: Site of Care and Benefit Preferences.
 */
const BenefitPreferences: React.FC = () => {
  const [modalState, setModalState] = useState<ModalState>({ type: 'none' });

  const sitesQuery = useSitesOfCare();
  const preferencesQuery = useBenefitPreferences();
  const drugsQuery = useMedicalBenefitDrugs();
  const deletePreference = useDeleteBenefitPreference({
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Benefit preference deleted</Toast>);
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const isLoading = sitesQuery.isPending || preferencesQuery.isPending;
  const isError = sitesQuery.isError || preferencesQuery.isError;

  if (isLoading) {
    return <LoadingSpinner height="200px" />;
  }

  if (isError) {
    return <PageLoadError />;
  }

  const sites = sitesQuery.data?.sites ?? [];
  const preferences = preferencesQuery.data?.preferences ?? [];
  const drugs = drugsQuery.data?.drugs ?? [];
  const hasSites = sites.length > 0;

  const closeModal = () => setModalState({ type: 'none' });

  const handleDuplicate = (preference: BenefitPreference) => {
    setModalState({
      type: 'addPreference',
      initialData: {
        drug_name: preference.drug_name,
        drug_ndc: preference.drug_ndc ?? undefined,
        rendering_provider_npi: preference.rendering_provider_npi ?? undefined,
        hcpcs: preference.hcpcs,
        cpt: preference.cpt,
        site_of_care_id: preference.site_of_care_id,
        benefits_preference: preference.benefits_preference,
      },
    });
  };

  return (
    <>
      <div className="flex max-w-[800px] flex-col gap-8">
        <div className="pt-2">
          <Typography styledAs="bodySmallDMSans">
            Configure site of care and drug fulfillment preferences to enable support for provider-administered
            treatments.
          </Typography>
          <Hairline $marginTop="16px" />
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Typography styledAs="bodyDMSans" weight={600}>
                Site of care
              </Typography>
              <Typography styledAs="bodySmallDMSans">
                Specify the locations where treatments will be administered.
              </Typography>
            </div>
            {hasSites && (
              <Button size="sm" onClick={() => setModalState({ type: 'addSite' })}>
                Add site
              </Button>
            )}
          </div>

          {hasSites ? (
            <SiteOfCareList sites={sites} />
          ) : (
            <div className="flex flex-col gap-4 rounded-medium border border-dashed border-grayscale-100 p-4">
              <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
                No sites have been added
              </Typography>
              <ButtonRow align={Alignment.Left}>
                <Button size="sm" onClick={() => setModalState({ type: 'addSite' })}>
                  Add site
                </Button>
              </ButtonRow>
            </div>
          )}
        </div>

        <Hairline />

        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <Typography styledAs="bodyDMSans" weight={600}>
                Fulfillment preferences
              </Typography>
              <Typography styledAs="bodySmallDMSans">
                For each drug formulation that may require medical benefit support, choose from the options below:
              </Typography>
              <ul className="ml-4 list-disc">
                <li>
                  <Typography styledAs="bodySmallDMSans">
                    <strong>Buy-and-bill:</strong> attempt buy-and-bill first by default
                  </Typography>
                </li>
                <li>
                  <Typography styledAs="bodySmallDMSans">
                    <strong>Specialty pharmacy:</strong> attempt pharmacy fulfillment (white bagging) first by default
                  </Typography>
                </li>
                <li>
                  <Typography styledAs="bodySmallDMSans">
                    <strong>Ask for each Rx:</strong> Tandem will request your input on each new prescription
                  </Typography>
                </li>
              </ul>
              <Typography styledAs="bodySmallDMSans">
                If no preference is set for a drug, Tandem will default to pharmacy fulfillment.
              </Typography>
            </div>
            <Button size="sm" onClick={() => setModalState({ type: 'addPreference' })} disabled={!hasSites}>
              Add preference
            </Button>
          </div>

          <BenefitPreferencesTable
            preferences={preferences}
            sites={sites}
            onEdit={(p) => setModalState({ type: 'editPreference', preference: p })}
            onDuplicate={handleDuplicate}
            onDelete={(p) => deletePreference.mutate(p.id)}
          />
        </div>
      </div>

      <AddSiteOfCareModal open={modalState.type === 'addSite'} onClose={closeModal} />

      {(modalState.type === 'addPreference' || modalState.type === 'editPreference') && (
        <BenefitPreferenceFormModal
          open
          onClose={closeModal}
          sites={sites}
          drugs={drugs}
          preference={modalState.type === 'editPreference' ? modalState.preference : undefined}
          initialData={modalState.type === 'addPreference' ? modalState.initialData : undefined}
        />
      )}
    </>
  );
};

export default BenefitPreferences;
